import { useQuery } from "@apollo/client";
import React, { useContext, useEffect, useState } from "react";
import { Container } from "../components/styled/ProductsStyled";

import { DELIVERY_STATUS, FILTER_ORDERID, ORDER_ID } from "../querys/index";
import { getImageFromDrive } from "../services/api";
import { Link, navigate } from "gatsby";

import noImage from "../images/icons/no-image.svg";
import { AuthUserContext } from "../context/AuthUser";
import { formatDate } from "../utils/format";
import { cleanUrl } from "../models/filter";

const InputField = ({ label, name, value, onChange, placeholder }) => (
  <div className="input-order-field">
    <label className="" htmlFor={name}>
      {label}
    </label>
    <input
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className=""
    />
  </div>
);

const OrderHistoryInfoDetail = ({ id }) => {
  const [orderID, setOrderID] = useState({});

  const { authUser } = useContext(AuthUserContext);
  const { loading: loadingFilter, error: ErrorFilter } = useQuery(
    FILTER_ORDERID,
    {
      variables: { usersPermissionsUserId: authUser?.user?.id },
      onCompleted: (data) => {
        const find_id_order =
          data.usersPermissionsUser.data.attributes.orderhistories.data.filter(
            (item) => item.id === id
          );
        if (find_id_order.length === 0) {
          return navigate("/order-history");
        }
      },
    }
  );

  const { loading, error } = useQuery(ORDER_ID, {
    variables: { orderhistoryId: id },
    onCompleted: (data) => {
      setOrderID(data);
    },
  });

  const initializeFormData = (data) => ({
    amount: data.amount || "",
    email: data.email || "",
    username: data.username || "",
    shippingFee: data.shippingFee || "",
    companyName: data.companyName || "",
    dateOrder: data.dateOrder || "",
    payment_method: data.payment_method || "",
    expectedDeliveryDate: data.expectedDeliveryDate || "",
    shippingDetails: data.shippingDetails || "",

    shippingAddress: {
      address: data.shippingAddress?.address || "",
      city: data.shippingAddress?.city || "",
      shippingUser: data.shippingAddress?.shippingUser || "",
      phoneNumber: data.shippingAddress?.phoneNumber || "",
      zipcode: data.shippingAddress?.zipcode || "",
      street: data.shippingAddress?.street || "",
    },
    detailProducts: Array.isArray(data.detailProducts)
      ? data.detailProducts.map((product) => ({
          articleNumber: product.articleNumber || "",
          colorNumber: product.colorNumber || "",
          quantity: product.quantity || "",
          totalPrice: product.totalPrice || "",
          regularPrice: product.regularPrice || "",
          image: product.image || "",
        }))
      : [],
    detailSwatch: Array.isArray(data.detailSwatch)
      ? data.detailSwatch.map((swatch) => ({
          articleNumber: swatch.articleNumber || "",
          colorNumber: swatch.colorNumber || "",
          quantity: swatch.quantity || "",
          totalPrice: swatch.totalPrice || null,
          regularPrice: swatch.regularPrice || null,
          image: swatch.image || "",
        }))
      : [],
    status: data.status || "",
  });

  const [formData, setFormData] = useState(
    initializeFormData(orderID?.orderhistory?.data.attributes || {})
  );
  const orderDetail =
    orderID?.orderhistory?.data?.attributes?.delivery_status?.data?.attributes;
  useEffect(() => {
    const newData = orderID?.orderhistory?.data.attributes || {};
    setFormData(initializeFormData(newData));
  }, [orderID]);

  const linkDHL = `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${orderDetail?.trackingNumber}`;
  const linkFedexUS = `https://www.fedex.com/wtrk/track/?action=track&tracknumbers=${orderDetail?.trackingNumber}&locale=en_us&cntry_code=us`;
  const linkFedexCA = `https://www.fedex.com/wtrk/track/?action=track&tracknumbers=${orderDetail?.trackingNumber}&locale=en_ca&cntry_code=ca`;
  const linkFedexOthers = `https://www.fedex.com/fedextrack/?tracknumbers=${orderDetail?.trackingNumber}`;
  const linkUPS = `https://www.ups.com/track?loc=en_US&tracknum=${orderDetail?.trackingNumber}`;
  const linkOther = orderDetail?.trackingUrl;

  let fedexLink;
  if (orderDetail?.country === "US") {
    fedexLink = linkFedexUS;
  } else if (orderDetail?.country === "CA") {
    fedexLink = linkFedexCA;
  } else {
    fedexLink = linkFedexOthers;
  }

  const formattedDate = orderDetail?.estimateDeliveryDate
    ? orderDetail.estimateDeliveryDate.split("-").reverse().join("-")
    : "";

  if (loading) return <p></p>;
  if (error) return <p>Error loading order data</p>;

  const renderTrackingLink = () => {
    if (!orderDetail?.trackingNumber) return null;

    let href;
    switch (orderDetail?.carrier) {
      case "dhl":
        href = linkDHL;
        break;
      case "fedex":
        href = fedexLink;
        break;
      case "ups":
        href = linkUPS;
        break;
      case "other":
        href = linkOther;
        break;
      default:
        return orderDetail?.trackingNumber || null;
    }

    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#5098ff" }}
      >
        {orderDetail?.trackingNumber}
      </a>
    );
  };

  return (
    <Container className="container">
      <div className="row">
        <h2
          className="font-semibold mt-4 u-margin-bottom-1"
          style={{ color: "#555555" }}
        >
          Order Detail #{id}
        </h2>
        <ul style={{ marginLeft: "2px" }}>
          <li style={{ marginBottom: "5px", color: "#454545" }}>
            <b style={{ color: "#555555" }}>Order Date: </b>{" "}
            {formatDate(orderID?.orderhistory?.data?.attributes?.dateOrder)}
          </li>

          <li style={{ marginBottom: "5px", color: "#454545" }}>
            <b style={{ color: "#555555" }}>Status: </b>
            <p
              style={{
                display: "inline",
                padding: "2px 8px",
              }}
              className={
                orderDetail?.status === "Ordered"
                  ? "order-status"
                  : orderDetail?.status === "Shipped (from Japan)"
                  ? "shipped-status"
                  : orderDetail?.status === "Shipped (arrived in your country)"
                  ? "shipped-status"
                  : orderDetail?.status === "Arrived"
                  ? "arrived-status"
                  : ""
              }
            >
              {orderDetail?.status}
            </p>
          </li>
          {orderDetail?.carrier && (
            <li
              style={{
                marginBottom: "5px",
                color: "#454545",
                textTransform: "capitalize",
              }}
            >
              <b style={{ color: "#555555" }}>Career: </b>{" "}
              {orderDetail?.carrier.toUpperCase()}
            </li>
          )}

          {/* <li style={{ marginBottom: "5px", color: "#454545" }}>
            <b style={{ color: "#555555" }}>Estimate delivery date: </b>{" "}
            {formattedDate}
          </li> */}
          {renderTrackingLink() && (
            <li
              style={{ marginBottom: "5px", color: "#454545", display: "flex" }}
            >
              <b style={{ color: "#555555" }}>Career's tracking website:</b>
              <div style={{ marginLeft: "5px" }}>{renderTrackingLink()}</div>
            </li>
          )}
        </ul>

        {formData?.detailProducts.length !== 0 ? (
          <div className="content-scroll-h">
            <h2 className="title-length-order">Length Order</h2>
            <table className="col-lg-12 col-md-12 table-order-content">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Article Number</th>
                  <th>Color Number</th>
                  <th>Quantity</th>
                  {/* <th>Total Price</th> */}
                </tr>
              </thead>
              <tbody>
                {formData?.detailProducts.map((product, i) => (
                  <tr key={i}>
                    <td>
                      {product.image ? (
                        <img
                          src={getImageFromDrive(product.image)}
                          alt={product.articleNumber}
                          className="thumbnail-order"
                        />
                      ) : (
                        ""
                      )}
                    </td>
                    <td>
                      <Link
                        to={`/products/${cleanUrl(product.articleNumber)}`}
                        style={{ marginLeft: "0px", color: "black" }}
                      >
                        {product.articleNumber}
                      </Link>
                    </td>
                    <td>{product.colorNumber.replaceAll('"', "")}</td>
                    <td>{product.quantity}</td>
                    {/* <td>${product?.totalPrice?.toFixed(2)}</td> */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          ""
        )}
        {formData?.detailSwatch !== 0 ? (
          <div className="content-scroll-h">
            <h2 className="title-length-order">Swatch Order</h2>
            <table className="col-lg-12 col-md-12 table-order-content">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Article Number</th>
                  <th>Color Number</th>
                  <th>Quantity</th>
                  {/* <th>Total Price</th> */}
                </tr>
              </thead>
              <tbody>
                {formData?.detailSwatch.map((product, i) => (
                  <tr key={i}>
                    <td>
                      {product.image ? (
                        <img
                          src={getImageFromDrive(product.image)}
                          alt={product.articleNumber}
                          className="thumbnail-order"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = noImage;
                          }}
                        />
                      ) : (
                        ""
                      )}
                    </td>
                    <td>
                      <Link
                        to={`/products/${cleanUrl(product.articleNumber)}`}
                        style={{ marginLeft: "0px", color: "black" }}
                      >
                        {product.articleNumber}
                      </Link>
                    </td>
                    <td>{product.colorNumber.replaceAll('"', "")}</td>
                    <td>{product.quantity}</td>
                    {/* <td>${product?.totalPrice?.toFixed(2)}</td> */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          ""
        )}

        {/* <DetailProducts
          products={formData?.detailProducts}
          onChange={handleChange}
        />

        <DetailSwatches
          swatches={formData?.detailSwatch}
          onChange={handleChange}
        /> */}
      </div>
    </Container>
  );
};
export default OrderHistoryInfoDetail;
